/**
 * Submitting the waitlist form.
 *
 * Same-origin, so no base URL and no CORS: the page and the API are one
 * deployment. The body is JSON in a `POST` — never a query string, because a
 * query string is written to server logs, browser history, and any referrer
 * header the page emits, and an email address does not belong in any of them.
 *
 * **Success is the server's word, never this file's.** The caller shows the
 * confirmation panel only for `ok`, and every other branch is a truthful
 * failure the visitor can act on. Nothing here optimistically assumes.
 */

export type WaitlistOutcome =
  | { readonly ok: true }
  /** The server refused the input. The form shows a field-level message. */
  | { readonly ok: false; readonly reason: 'rejected' }
  /** Too many attempts from this address. */
  | { readonly ok: false; readonly reason: 'rate_limited' }
  /** Offline, DNS failure, timeout, 5xx — anything that is not the visitor's
      fault and is worth retrying. */
  | { readonly ok: false; readonly reason: 'unavailable' }
  /** The request was aborted, normally because the component unmounted. The
      caller renders nothing for this. */
  | { readonly ok: false; readonly reason: 'aborted' };

export interface WaitlistSubmission {
  readonly email: string;
  readonly storeUrl: string;
  /** The honeypot. Always empty when a person fills the form in. */
  readonly website: string;
}

export const WAITLIST_ENDPOINT = '/public/waitlist';

export async function submitWaitlist(
  submission: WaitlistSubmission,
  signal: AbortSignal,
): Promise<WaitlistOutcome> {
  let response: Response;
  try {
    response = await fetch(WAITLIST_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        email: submission.email,
        // Omitted rather than sent empty. The server treats both the same, but
        // sending a key with nothing in it says something was meant.
        ...(submission.storeUrl.trim().length > 0
          ? { store_url: submission.storeUrl.trim() }
          : {}),
        ...(submission.website.length > 0 ? { website: submission.website } : {}),
      }),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, reason: 'aborted' };
    }
    return { ok: false, reason: 'unavailable' };
  }

  if (response.ok) return { ok: true };
  if (response.status === 429) return { ok: false, reason: 'rate_limited' };
  if (response.status >= 400 && response.status < 500) {
    return { ok: false, reason: 'rejected' };
  }
  return { ok: false, reason: 'unavailable' };
}
